"use client";

import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useCheckoutGiftCard } from "../hooks/use-checkout-giftcard";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { GiftcardDetailsStep } from "../giftcard-details-step";
import { PersonalDetailsStep } from "../personal-details-step";
import { formatPrice } from "../utils";
import { validateGuest } from "../validation";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";

export type CheckoutGiftCardProps = {
  // Data
  product: any;
  school?: any;
  user?: any;
  amount?: number;
  /** Gift card design images to choose from */
  images?: string[];

  // Callbacks
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  /** Callback fired when step changes (for scroll-to-top behavior) */
  onStepChange?: (step: string) => void;

  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** When true, shows the giftcard-details step first (for webcomponents/widget) */
  showGiftcardDetailsStep?: boolean;

  // Basket feature
  basketItemCount?: number;
  onAddToBasket?: (itemData: any) => void;
  onProceedToBasketCheckout: (itemData: any) => void;
  /** When true, skip the basket choice UI and auto-fire onProceedToCheckout on mount (single-item direct lane). */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

export function CheckoutGiftCard({
  product,
  school,
  user,
  amount: initialAmount,
  images = [],
  onError,
  onClose,
  onStepChange,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  isEmbedMode = false,
  showGiftcardDetailsStep = false,
  basketItemCount = 0,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
}: CheckoutGiftCardProps) {
  const checkout = useCheckoutGiftCard({
    product,
    school,
    user,
    initialAmount,
    showGiftcardDetailsStep,
  });

  // Validation errors
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [giftcardTouched, setGiftcardTouched] = useState<Record<string, boolean>>({});

  const errors = {
    email:
      touched.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkout.personalDetails.email)
        ? "Please enter a valid email"
        : undefined,
    firstName:
      touched.firstName && !checkout.personalDetails.firstName.trim()
        ? "First name is required"
        : undefined,
    lastName:
      touched.lastName && !checkout.personalDetails.lastName.trim()
        ? "Last name is required"
        : undefined,
  };

  const giftcardErrors = useMemo(() => ({
    amount: checkout.amount < 500 ? "Minimum amount is £5" : undefined,
    recipientName: !checkout.recipientName?.trim() ? "Recipient name is required" : undefined,
    senderName: !checkout.senderName?.trim() ? "Your name is required" : undefined,
    recipientEmail: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkout.recipientEmail || "")
      ? "Valid email is required" : undefined,
  }), [checkout.amount, checkout.recipientName, checkout.senderName, checkout.recipientEmail]);

  // Call step change callback for scroll-to-top behavior
  useEffect(() => {
    onStepChange?.(checkout.step);
  }, [checkout.step, onStepChange]);

  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket) return;
    onAddToBasket({
      type: "giftcard",
      productId: product._id || product.id,
      productName: `${formatPrice(checkout.amount, currency)} Gift Card`,
      quantity: 1,
      price: checkout.amount,
      currency,
      imageUrl: product.images?.[0]?.url,
      // The artwork the buyer actually picked in the gift-card details step.
      // `imageUrl` is only the product's first image, so without this the
      // picker is decorative: every consumer downstream falls back to image
      // #1. Rides all the way to giftCard.details.cover (the field the
      // gift-card emails render) via the basket item's imageUrl.
      selectedImage: checkout.selectedImage,
      recipientEmail: checkout.recipientEmail,
      recipientName: checkout.recipientName,
      senderName: checkout.senderName,
      message: checkout.message,
      guestDetails: {
        firstName: checkout.personalDetails.firstName || "",
        lastName: checkout.personalDetails.lastName || "",
        email: checkout.personalDetails.email || "",
        phone: "",
      },
      checkoutGuestsInformation: [{
        firstName: checkout.personalDetails.firstName || "",
        lastName: checkout.personalDetails.lastName || "",
        email: checkout.personalDetails.email || "",
        phoneNumber: "",
      }],
    });
  }, [onAddToBasket, product, checkout.amount, checkout.selectedImage, checkout.recipientEmail, checkout.recipientName, checkout.senderName, checkout.message, checkout.personalDetails, currency]);

  const handleProceedToBasketCheckout = useCallback(() => {
    onProceedToBasketCheckout({
      type: "giftcard",
      productId: product._id || product.id,
      productName: `${formatPrice(checkout.amount, currency)} Gift Card`,
      quantity: 1,
      price: checkout.amount,
      currency,
      imageUrl: product.images?.[0]?.url,
      // See handleAddToBasket: the picked artwork, not just image #1.
      selectedImage: checkout.selectedImage,
      recipientEmail: checkout.recipientEmail,
      recipientName: checkout.recipientName,
      senderName: checkout.senderName,
      message: checkout.message,
      guestDetails: {
        firstName: checkout.personalDetails.firstName || "",
        lastName: checkout.personalDetails.lastName || "",
        email: checkout.personalDetails.email || "",
        phone: "",
      },
      checkoutGuestsInformation: [{
        firstName: checkout.personalDetails.firstName || "",
        lastName: checkout.personalDetails.lastName || "",
        email: checkout.personalDetails.email || "",
        phoneNumber: "",
      }],
    });
  }, [onProceedToBasketCheckout, product, checkout.amount, checkout.selectedImage, checkout.recipientEmail, checkout.recipientName, checkout.senderName, checkout.message, checkout.personalDetails, currency]);

  // Handle continue with validation
  const handleContinue = useCallback(() => {
    if (checkout.step === "giftcard-details") {
      setGiftcardTouched({
        amount: true,
        recipientName: true,
        senderName: true,
        recipientEmail: true
      });
      if (!checkout.canContinue()) return;
    }

    if (checkout.step === "personal") {
      setTouched({ firstName: true, lastName: true, email: true });
      if (!checkout.canContinue()) return;
    }

    checkout.goToNextStep();
  }, [checkout]);

  return (
    <>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title="Gift Card"
          subtitle={product.title || "Purchase Gift Card"}
          onClose={onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Step Navigation */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              {checkout.visibleSteps.map((stepName, index) => {
                // Format step names for display
                const displayName = stepName === "giftcard-details"
                  ? "Gift Card"
                  : stepName === "personal"
                    ? "Your Details"
                    : stepName;

                return (
                  <React.Fragment key={stepName}>
                    {index > 0 && (
                      <span className="text-muted-foreground/70">/</span>
                    )}
                    <span
                      className={`${
                        index === checkout.currentStepIndex
                          ? "text-primary font-medium"
                          : index < checkout.currentStepIndex
                            ? "text-muted-foreground"
                            : "text-muted-foreground/70"
                      }`}
                    >
                      {displayName}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Giftcard Details Step */}
            {checkout.step === "giftcard-details" && (
              <GiftcardDetailsStep
                values={checkout.giftcardDetailsValues}
                currency={currency}
                images={images}
                onChange={(field, value) => {
                  checkout.updateGiftcardDetails(field, value);
                }}
                onBlur={(field) => {
                  setGiftcardTouched((prev) => ({ ...prev, [field]: true }));
                }}
                errors={giftcardErrors}
                touched={giftcardTouched}
              />
            )}

            {/* Personal Details Step */}
            {checkout.step === "personal" && (
              <PersonalDetailsStep
                showPhone={false}
                values={{
                  firstName: checkout.personalDetails.firstName,
                  lastName: checkout.personalDetails.lastName,
                  email: checkout.personalDetails.email,
                  phone: "",
                  marketingOptIn: checkout.personalDetails.marketingOptIn,
                }}
                onChange={(field, value) => {
                  checkout.updatePersonalDetails(field as any, value);
                }}
                onBlur={(field) => {
                  setTouched((prev) => ({ ...prev, [field]: true }));
                }}
                errors={errors}
                touched={touched}
                businessCountry={school?.businessCountry}
              />
            )}

            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={`${formatPrice(checkout.amount, currency)} Gift Card`}
                total={checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}
          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title="Gift Card"
              items={[
                {
                  name: checkout.amount > 0
                    ? `${formatPrice(checkout.amount, currency)} Gift Card`
                    : "Gift Card",
                  price: checkout.amount,
                  count: 1,
                },
              ]}
              total={checkout.total}
              currency={currency}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
            />
            {/* Show gift card details in sidebar if filled */}
            {checkout.recipientName && (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">To:</span>
                  <span className="font-medium">{checkout.recipientName}</span>
                </div>
                {checkout.senderName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-medium">{checkout.senderName}</span>
                  </div>
                )}
                {checkout.recipientEmail && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Send to:</span>
                    <span className="font-medium truncate max-w-[150px]">{checkout.recipientEmail}</span>
                  </div>
                )}
              </div>
            )}
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      <CheckoutFooter
        total={checkout.total}
        currency={currency}
        showBack={!checkout.isFirstStep}
        onBack={checkout.goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={handleContinue}
        continueText="Continue"
        continueDisabled={!checkout.canContinue()}
        isEmbedMode={isEmbedMode}
      />
    </>
  );
}
